import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import ProgramEmailTemplates from '../../components/admin/ProgramEmailTemplates';
import ProgramFcmCredentials from '../../components/admin/ProgramFcmCredentials';
import { programsAdminAPI } from '../../services/api';
import '../../styles/admin-shared.css';

const EMPTY = {
  slug: '',
  label: '',
  description: '',
  domain: '',
  primary_color: '#1A95FF',
  secondary_color: '#10083B',
  success_color: '#20FF80',
  danger_color: '#FF4545',
  text_primary: '#FAFAFA',
  text_secondary: '#CFCFCF',
  logo_url: '',
  icon_url: '',
  favicon_url: '',
  hero_mockup_url: '',
  app_id_ios: '',
  app_id_android: '',
  blocker_strategy: 'none',
  default_addiction_type: '',
  hero_title: '',
  hero_subtitle: '',
  hero_cta_label: '',
  hero_cta_url: '',
  about_markdown: '',
  terms_markdown: '',
  privacy_markdown: '',
  is_active: true,
};

const ProgramEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await programsAdminAPI.getById(id);
      const data = res.data?.data || {};
      setForm({ ...EMPTY, ...data });
    } catch (err) {
      console.error('Erro ao carregar programa:', err);
      setError('Não foi possível carregar o programa.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Strip empty optional strings to nulls so backend keeps them null,
      // never an empty string (which can collide with @unique constraints).
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === 'string' && v.trim() === '') {
          payload[k] = isNew ? undefined : null;
        } else {
          payload[k] = v;
        }
      }

      if (isNew) {
        const res = await programsAdminAPI.create(payload);
        navigate(`/admin/programs/${res.data.data.id}/edit`);
      } else {
        await programsAdminAPI.update(id, payload);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Falha ao salvar programa';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: 40 }}>Carregando...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="users-page">
        <div className="page-header">
          <div>
            <button
              type="button"
              className="btn btn-ghost btn-with-icon"
              onClick={() => navigate('/admin/programs')}
            >
              <ArrowLeft size={16} /> Programas
            </button>
            <h1 style={{ marginTop: 8 }}>{isNew ? 'Novo Programa' : `Editar: ${form.label}`}</h1>
          </div>
          <button
            type="submit"
            form="program-form"
            className="btn btn-primary btn-with-icon"
            disabled={saving}
          >
            <Save size={16} /> {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        <form id="program-form" onSubmit={handleSubmit} className="admin-form">
          <section className="form-section">
            <h2>Identificação</h2>
            <div className="form-grid">
              <label>
                <span>Slug *</span>
                <input
                  type="text"
                  value={form.slug}
                  disabled={!isNew}
                  onChange={(e) => update('slug', e.target.value.toLowerCase())}
                  placeholder="freedom"
                  required
                />
                <small>Imutável após criação. Letras, números e hífen.</small>
              </label>
              <label>
                <span>Nome de exibição *</span>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => update('label', e.target.value)}
                  required
                />
              </label>
              <label className="span-2">
                <span>Descrição curta</span>
                <textarea
                  rows={2}
                  value={form.description || ''}
                  onChange={(e) => update('description', e.target.value)}
                />
              </label>
              <label>
                <span>Domínio</span>
                <input
                  type="text"
                  value={form.domain || ''}
                  onChange={(e) => update('domain', e.target.value)}
                  placeholder="freedom.lifyup.com.br"
                />
              </label>
              <label>
                <span>Tipo de vício padrão</span>
                <input
                  type="text"
                  value={form.default_addiction_type || ''}
                  onChange={(e) => update('default_addiction_type', e.target.value)}
                  placeholder="pornografia"
                />
              </label>
              <label>
                <span>Estratégia do bloqueador</span>
                <select
                  value={form.blocker_strategy}
                  onChange={(e) => update('blocker_strategy', e.target.value)}
                >
                  <option value="none">Nenhuma</option>
                  <option value="porn">Pornografia</option>
                  <option value="gambling">Apostas</option>
                  <option value="distraction">Distração</option>
                  <option value="custom">Personalizada</option>
                </select>
              </label>
              <label>
                <span>Ativo</span>
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => update('is_active', e.target.checked)}
                />
              </label>
            </div>
          </section>

          <section className="form-section">
            <h2>Marca</h2>
            <div className="form-grid">
              {[
                ['primary_color', 'Cor primária'],
                ['secondary_color', 'Cor secundária'],
                ['success_color', 'Cor de sucesso'],
                ['danger_color', 'Cor de alerta'],
                ['text_primary', 'Texto primário'],
                ['text_secondary', 'Texto secundário'],
              ].map(([key, label]) => (
                <label key={key}>
                  <span>{label}</span>
                  <input
                    type="color"
                    value={form[key] || '#000000'}
                    onChange={(e) => update(key, e.target.value)}
                  />
                </label>
              ))}
            </div>
          </section>

          <section className="form-section">
            <h2>Assets</h2>
            <div className="form-grid">
              {[
                ['logo_url', 'Logo URL'],
                ['icon_url', 'Ícone URL'],
                ['favicon_url', 'Favicon URL'],
                ['hero_mockup_url', 'Mockup do hero (URL)'],
                ['app_id_ios', 'App ID iOS'],
                ['app_id_android', 'App ID Android'],
              ].map(([key, label]) => (
                <label key={key}>
                  <span>{label}</span>
                  <input
                    type="text"
                    value={form[key] || ''}
                    onChange={(e) => update(key, e.target.value)}
                  />
                </label>
              ))}
            </div>
          </section>

          <section className="form-section">
            <h2>Hero (landing)</h2>
            <div className="form-grid">
              <label className="span-2">
                <span>Título</span>
                <input
                  type="text"
                  value={form.hero_title || ''}
                  onChange={(e) => update('hero_title', e.target.value)}
                />
              </label>
              <label className="span-2">
                <span>Subtítulo</span>
                <textarea
                  rows={2}
                  value={form.hero_subtitle || ''}
                  onChange={(e) => update('hero_subtitle', e.target.value)}
                />
              </label>
              <label>
                <span>CTA label</span>
                <input
                  type="text"
                  value={form.hero_cta_label || ''}
                  onChange={(e) => update('hero_cta_label', e.target.value)}
                />
              </label>
              <label>
                <span>CTA URL</span>
                <input
                  type="text"
                  value={form.hero_cta_url || ''}
                  onChange={(e) => update('hero_cta_url', e.target.value)}
                />
              </label>
            </div>
          </section>

          <section className="form-section">
            <h2>Conteúdo (Markdown)</h2>
            <div className="form-grid">
              <label className="span-2">
                <span>Sobre</span>
                <textarea
                  rows={6}
                  value={form.about_markdown || ''}
                  onChange={(e) => update('about_markdown', e.target.value)}
                />
              </label>
              <label className="span-2">
                <span>Termos</span>
                <textarea
                  rows={6}
                  value={form.terms_markdown || ''}
                  onChange={(e) => update('terms_markdown', e.target.value)}
                />
              </label>
              <label className="span-2">
                <span>Privacidade</span>
                <textarea
                  rows={6}
                  value={form.privacy_markdown || ''}
                  onChange={(e) => update('privacy_markdown', e.target.value)}
                />
              </label>
            </div>
          </section>
        </form>

        {!isNew && id && (
          <>
            <ProgramEmailTemplates programId={id} />
            <ProgramFcmCredentials
              programId={id}
              hasCredentials={!!form.hasFcmCredentials}
              projectId={form.fcmProjectId}
              onUpdated={load}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ProgramEditPage;
